import { skipToken } from "@reduxjs/toolkit/query";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { useGetEntityAgreementByIdQuery } from "legal-entities/agreements/_api/entity-agreement-api-service";
import { forumDialogPath } from "messaging/conversations/conversation-links";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import ExportGrantsButton from "permissions/_views/export/export-grants-button";
import type { RecordOpening } from "permissions/_api/permission-targets-api-service";
import { kindPermissionsPath } from "permissions/_views/kinds/permission-kinds-model";
import { permissionAreaToken } from "permissions/_views/permission-areas";
import RecordAccessPanel from "permissions/_views/records/record-access-panel";
import { RecordCaption, recordKindOf, recordTitle } from "permissions/_views/records/record-caption";
import RecordLoadFailure from "permissions/_views/records/record-load-failure";
import {
  type RecordDomain,
  type RecordPermissionsFrom,
  hasRelatedRecords,
  isRecordDomain,
  recordPermissionsFrom,
} from "permissions/_views/records/record-permissions-path";
import RelatedRecordGroups from "permissions/_views/records/related-record-groups";
import useRecordGrants from "permissions/_views/records/use-record-grants";
import PermissionsSubjectHeader, { type PermissionsCrumb } from "permissions/_views/scaffold/permissions-subject-header";
import { personNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import { type NameState, personReference } from "permissions/_views/targets/permission-target-state";
import { grantDate, kindLabel, recordPage } from "permissions/_views/wording/permission-wording";
import useSessionUser from "permissions/session/use-session-user";
import { type ReactNode, useMemo } from "react";
import { Link as RouterLink, useLocation, useParams, useSearchParams } from "react-router";

/**
 * Who holds a grant on one record, at `/permissions/records/{domain}/{id}` (`docs/design/
 * permissions/PERMISSIONS_UI_DESIGN.md`, "The record page"). Conversations first (#6171).
 *
 * The trail runs Permissions, then the record's kind, then the record; a page reached from a record's
 * own screen puts that screen where the kind would be, so the crumb is the one way back (#6838). The
 * header names the record and where it lives, and links to it; the caption counts the
 * people who hold a live grant on it. A record the viewer cannot read, or one that no longer exists,
 * keeps the same target-state header, and its holders are still listed: an administrator manages
 * access to records they cannot open themselves. Documents, proposals and work agreements are on the
 * same page (#6172); a proposal's and a work agreement's own conversations and documents are groups
 * below its rows.
 */

const CONTENT_MAX = 1100;

const Page = styled(Box)({ height: "100%", minHeight: 0, overflowY: "auto", overflowX: "hidden" });

const Content = styled(Box)(({ theme }) => ({
  maxWidth: CONTENT_MAX,
  margin: "0 auto",
  padding: theme.spacing(2),
  display: "grid",
  gap: theme.spacing(2),
  [theme.breakpoints.up("md")]: { padding: theme.spacing(2, 3) },
}));

/** Where a domain's records live in the app, for the header's second line. */
const RECORD_HOMES: Readonly<Record<RecordDomain, string>> = {
  forums: "In Communications",
  documents: "In Documents",
  proposals: "In Proposals",
  "work-agreements": "In Work",
  "calendar-events": "In Calendar",
  "organization-agreements": "In Organization",
  "fee-schedule-templates": "In Fee schedules",
  "team-templates": "In Teams",
};

/**
 * Where a domain's record opens, when the page can say without reading it. A proposal opens on a
 * route that depends on its stage and the reader's part in it, and a document and a calendar event
 * have no page of their own, so those are reached back through where the reader came from.
 */
export function recordHomePath(domain: RecordDomain, recordId: string): string | undefined {
  switch (domain) {
    case "forums":
      return forumDialogPath(recordId);
    case "work-agreements":
      return `/work/active/view/${recordId}/details`;
    case "organization-agreements":
      return `/entity-agreements/${recordId}`;
    case "fee-schedule-templates":
      return `/fee-schedules/templates/view/${recordId}`;
    case "team-templates":
      return `/teams/templates/view/${recordId}`;
    case "documents":
    case "proposals":
    case "calendar-events":
      return undefined;
  }
}

/**
 * The crumbs before the record's name: Permissions, then the screen the reader came from, or, when
 * they came from within Permissions, the record's kind page. The crumb is the page's only way back
 * (`PERMISSIONS_UI_DESIGN.md`, "Page header, search and back").
 */
export function recordTrail(kind: Pick<PermissionKind, "name" | "slug">, from: RecordPermissionsFrom | undefined, noun: string): PermissionsCrumb[] {
  const origin = from
    ? { label: recordPage.fromCrumb(from.label ?? noun), to: `${from.pathname}${from.search ?? ""}` }
    : { label: kindLabel(kind), to: kindPermissionsPath(kind) };
  return [{ label: "Permissions", to: "/permissions" }, origin];
}

/** "· opened 2 Sep by Sam Ode": the day, and the person once the directory names them. */
function RecordOpened(props: Readonly<{ opened: RecordOpening }>) {
  const { byUserId } = props.opened;
  // Memoized on purpose: a fresh set each render would be a fresh lookup argument.
  const userIds = useMemo(() => new Set(byUserId ? [byUserId] : []), [byUserId]);
  const names = usePersonNames(userIds);
  const date = grantDate(new AHDate(props.opened.date), AHDate.TODAY);
  const by = byUserId ? personNameState(names, byUserId) : undefined;
  return <>{` · ${recordPage.opened(date, by && by.state !== "loading" ? personReference(by) : undefined)}`}</>;
}

/** "· Open the conversation": a link to the record itself, when the page can say where it opens and the viewer may read it. */
function RecordLink(props: Readonly<{ domain: RecordDomain; recordId: Guid; target: NameState; noun: string }>) {
  // An organization agreement is named through the admin-gated targets read, which does not say the
  // viewer may open it: the agreement's own read asserts EntityAgreementAccess. So its link waits for
  // that read to answer (#6503 review). The other kinds are named by reads that ask read access.
  const asksAgreement = props.domain === "organization-agreements" && props.target.state === "found";
  const agreement = useGetEntityAgreementByIdQuery(asksAgreement ? { id: props.recordId } : skipToken);
  const home = recordHomePath(props.domain, props.recordId.value);
  if (props.target.state !== "found" || !home) return null;
  if (props.domain === "organization-agreements" && !agreement.isSuccess) return null;
  return (
    <>
      {" · "}
      <Link component={RouterLink} to={home} underline="hover">
        {recordPage.openRecord(props.noun)}
      </Link>
    </>
  );
}

function RecordPermissions(props: Readonly<{ domain: RecordDomain; recordId: Guid; kind: PermissionKind; kinds: readonly PermissionKind[] }>) {
  const { domain, recordId, kind, kinds } = props;
  const noun = kind.targetNoun ?? "record";
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const pageIndex = Math.max(0, Number.parseInt(searchParams.get("page") ?? "1", 10) - 1 || 0);
  const grants = useRecordGrants(kind, recordId, pageIndex);
  const token = permissionAreaToken(kind.area);
  const from = recordPermissionsFrom(location.state);

  let targetNote: ReactNode = null;
  if (grants.target.state === "failed") {
    targetNote = (
      <Button size="small" onClick={grants.retryTarget}>
        Retry
      </Button>
    );
  }

  return (
    <Page>
      <Content>
        <PermissionsSubjectHeader
          trail={recordTrail(kind, from, noun)}
          title={recordTitle(grants.target, noun)}
          actions={
            <ExportGrantsButton
              scope={{ subject: "record", kind, recordId }}
              kinds={kinds}
              // A record the viewer cannot name gives its file the kind's noun and its id.
              subject={grants.target.state === "found" ? grants.target.name : `${noun} ${recordId.value}`}
            />
          }
          avatar={
            <Avatar sx={{ bgcolor: "transparent", border: 1, borderColor: "divider" }}>
              <token.Icon sx={{ color: token.color }} aria-hidden />
            </Avatar>
          }
          secondary={
            <>
              {`${noun.charAt(0).toUpperCase()}${noun.slice(1)} · ${RECORD_HOMES[domain]}`}
              {grants.opened && <RecordOpened opened={grants.opened} />}
              <RecordLink domain={domain} recordId={recordId} target={grants.target} noun={noun} />
              {targetNote}
            </>
          }
        >
          <RecordCaption domain={domain} grants={grants} noun={noun} />
        </PermissionsSubjectHeader>
        <RecordAccessPanel
          kind={kind}
          kinds={kinds}
          recordId={recordId}
          recordName={grants.target.state === "found" ? grants.target.name : undefined}
          grants={grants}
          pageIndex={pageIndex}
          // The location state carries where the reader came from, so paging keeps the crumb back there.
          onPageChange={(index) => setSearchParams(index === 0 ? {} : { page: String(index + 1) }, { state: location.state })}
          idPrefix="record-page"
        />
        {hasRelatedRecords(domain) && <RelatedRecordGroups domain={domain} recordId={recordId.value} noun={noun} kinds={kinds} />}
      </Content>
    </Page>
  );
}

/**
 * The route: gates on Member Permissions, checks the address names a record domain and an id, and
 * waits for the kinds, which name the page.
 */
export default function RecordPermissionsPage() {
  const { domain, recordId } = useParams();
  const sessionUser = useSessionUser();
  const kindsQuery = useGetPermissionKindsQuery();

  if (!sessionUser) return null;
  if (!sessionUser.canReadMemberPermissions()) return <NotAuthorized />;
  if (!isRecordDomain(domain) || !recordId || !Guid.isValid(recordId)) {
    return <NotAuthorized message="There is no record at this address." />;
  }
  if (kindsQuery.error) {
    return (
      <Box sx={{ m: 2 }}>
        <RecordLoadFailure
          title={recordPage.kindsFailed}
          detail={getErrorMessageFromUnknown(kindsQuery.error)}
          onRetry={() => void kindsQuery.refetch()}
        />
      </Box>
    );
  }
  if (!kindsQuery.data) return <Skeleton variant="rectangular" height={120} sx={{ m: 2 }} />;

  const kind = recordKindOf(kindsQuery.data.kinds, domain);
  if (!kind) return <NotAuthorized message="There is no record at this address." />;
  return <RecordPermissions key={`${domain}-${recordId}`} domain={domain} recordId={new Guid(recordId)} kind={kind} kinds={kindsQuery.data.kinds} />;
}
